import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { openChatWindow } from '@features/front-chat';
import { useClinicians } from '@features/organizations';
import {
  ArtifactDropOverlay,
  formatUploadLimitError,
  useAdminArtifactUploadConstraints,
  useAdminOrganizationArtifactEventStream,
  useArtifactDownload,
  useArtifactUploadConstraints,
  useChartWideDropZone,
  useOptimisticUploads,
  useOrganizationArtifactEventStream,
  useUploadAdminOrganizationArtifacts,
  useUploadOrganizationArtifacts,
  validateFiles,
} from '@features/patient-artifacts';
import { useCan, useUserRoles } from '@features/user';
import { useAutoSave } from '@hooks/useAutoSave';
import { Alert, Box, Button, LinearProgress } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';
import { normalizeProviderLicenseTypes } from '@utils/providerLicenseTypes';
import { ROLES } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import { getIntakeFieldFormatError } from '../../fieldFormatValidation';
import {
  acknowledgePersistedFieldValue,
  defaultFieldValue,
  durableRequiredCompletion,
  findIntakeItemForField,
  hydrateFieldStates,
  type IntakeFieldStates,
  updateFieldStatesForDraftChange,
} from '../../fieldPersistence';
import { useSerializedIntakeWrites } from '../../hooks/useSerializedIntakeWrites';
import { intakePagePath, resolveIntakePageKey } from '../../routing';
import {
  useAcceptIntakeHubDocumentFieldSuggestionsMutation,
  useAddAdminIntakeProviderRosterEntryMutation,
  useAddIntakeProviderRosterEntryMutation,
  useGenerateAdminIntakeFormMutation,
  useGenerateIntakeFormMutation,
  useGetAdminIntakeFormConfigurationQuery,
  useGetAdminIntakeFormPayerOptionsQuery,
  useGetAdminIntakeHubDashboardQuery,
  useGetAdminIntakeHubDocumentsQuery,
  useGetIntakeFormConfigurationQuery,
  useGetIntakeFormPayerOptionsQuery,
  useGetIntakeHubDashboardQuery,
  useGetIntakeHubDocumentsQuery,
  useRemoveAdminIntakeProviderRosterEntryMutation,
  useRemoveIntakeProviderRosterEntryMutation,
  useUpdateAdminIntakeFormConfigurationMutation,
  useUpdateAdminIntakeHubItemEvidenceMutation,
  useUpdateIntakeFormConfigurationMutation,
  useUpdateIntakeHubItemEvidenceMutation,
  useUpdateIntakeHubSensitiveFieldMutation,
} from '../../store/api';
import { SuggestionAcceptanceError } from '../../suggestionAcceptance';
import type {
  GeneratedIntakeField,
  IntakeFieldSuggestion,
  IntakeFieldSuggestionAcceptanceRequest,
  IntakeFieldSuggestionAcceptanceResponse,
  IntakeFieldValue,
  IntakeFormConfiguration,
  IntakeFormConfigurationUpdate,
  IntakeHubDocumentInventoryItem,
  IntakePayerRosterEntry,
} from '../../types';
import {
  canDisplayDocumentSuggestion,
  intakeDocumentInventoryItemToDetail,
} from '../intakeDocumentDetail';

import { type EligibleProvider, type IntakeConfig, IntakeConfigPage } from './IntakeConfigPage';
import { type FoundDocument, IntakeDocumentsPage } from './IntakeDocumentsPage';
import { IntakeFieldsPage } from './IntakeFieldsPage';
import { type ForecastDocument, IntakeForecastPage } from './IntakeForecastPage';
import { IntakePayersPage } from './IntakePayersPage';
import { IntakeReviewSubmitPage } from './IntakeReviewSubmitPage';
import { IntakeShell, type RailProgressState } from './IntakeShell';
import { RAIL_SECTIONS } from './navigation';

const ORDERED_KEYS = RAIL_SECTIONS.flatMap(section => section.items.map(item => item.pageKey));

export type IntakeExperienceContext =
  | { kind: 'provider' }
  | {
      kind: 'admin';
      organizationId: string;
      userId?: string;
      providerEmail?: string;
      eligibleProviders?: EligibleProvider[];
    };

interface IntakeExperienceProps {
  context?: IntakeExperienceContext;
  onDirtyChange?: (isDirty: boolean) => void;
}

const PROVIDER_CONTEXT: IntakeExperienceContext = { kind: 'provider' };

/** Documents pages get the drop zone; every other page renders fields. */
const DOCUMENT_PAGES: Record<string, { title: string; sub: string; hint: string }> = {
  M1: {
    title: 'Clinic documents',
    sub: 'Upload your W-9 here so it is included with your intake.',
    hint: "PDF or photo · You'll enter and confirm its details on the next pages",
  },
  P1: {
    title: 'Your documents',
    sub: 'Upload your supporting documents here so they are included with your intake.',
    hint: "PDF or photo · You'll enter and confirm their details on the next pages",
  },
};

function documentDisplayStatus(
  processingStatus?: string | null,
): 'parsing' | 'uploaded' | 'failed' {
  if (processingStatus === 'failed') return 'failed';
  if (processingStatus === 'enriched' || processingStatus === 'processed') return 'uploaded';
  return 'parsing';
}

function persistedDocumentToFoundDocument(
  documentItem: IntakeHubDocumentInventoryItem,
  fields: GeneratedIntakeField[],
): FoundDocument {
  const documentReference = documentItem.document_reference;
  return {
    artifactId: documentReference.source_artifact_id ?? null,
    filename: documentReference.title ?? 'Untitled document',
    status: documentDisplayStatus(documentReference.processing_status),
    docKind: documentReference.doc_kind ?? null,
    filledCount:
      (documentItem.field_suggestions?.length ?? 0) + (documentItem.extracted_fields?.length ?? 0),
    understood: documentReference.processing_status !== 'failed',
    detail: intakeDocumentInventoryItemToDetail(documentItem, fields),
  };
}

function pageLabel(pageKey: string): string {
  return (
    RAIL_SECTIONS.flatMap(section => section.items).find(item => item.pageKey === pageKey)?.label ??
    pageKey
  );
}

function defaultIntakeConfig(): IntakeConfig {
  return {
    role: 'manager_who_also_practices',
    products: [],
    isPrescriber: true,
    licenseTypes: ['PMHNP'],
    practiceType: 'group',
    payerKeys: [],
  };
}

function serverConfigurationToIntakeConfig(configuration: IntakeFormConfiguration): IntakeConfig {
  return {
    role: configuration.role,
    products: configuration.products ?? [],
    isPrescriber: configuration.is_prescriber ?? false,
    licenseTypes: normalizeProviderLicenseTypes(configuration.license_types ?? []) ?? [],
    practiceType: configuration.practice_type ?? 'group',
    payerKeys: (configuration.payers ?? []).map(payer => payer.payer_key),
  };
}

function intakeConfigurationUpdate(config: IntakeConfig): IntakeFormConfigurationUpdate {
  return {
    role: config.role,
    practice_type: config.practiceType,
    is_prescriber: config.isPrescriber,
    license_types: config.licenseTypes,
    payers: config.payerKeys.map(payer_key => ({ payer_key, states: [], plan_types: [] })),
  };
}

function configurationsMatch(left: IntakeConfig, right: IntakeConfig): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function suggestionIdentity(suggestion: IntakeFieldSuggestion): string {
  return [suggestion.target_intake_item_id, suggestion.extraction_id, suggestion.field_id].join(
    ':',
  );
}

function acceptanceRequestIdentity(request: IntakeFieldSuggestionAcceptanceRequest): string {
  return JSON.stringify([
    request.intake_item_id,
    request.document_reference_id,
    request.extraction_id,
    request.field_id,
  ]);
}

function acceptanceOperationIdentity(
  kind: 'individual' | 'bulk',
  requests: IntakeFieldSuggestionAcceptanceRequest[],
): string {
  return `${kind}:${JSON.stringify(requests.map(acceptanceRequestIdentity).sort())}`;
}

function hasExactlyCorrelatedAcceptanceResults(
  requests: IntakeFieldSuggestionAcceptanceRequest[],
  response: unknown,
): response is IntakeFieldSuggestionAcceptanceResponse {
  if (!response || typeof response !== 'object') return false;
  const results = (response as { results?: unknown }).results;
  if (!Array.isArray(results) || results.length !== requests.length) return false;

  return results.every((result: unknown, index) => {
    if (!result || typeof result !== 'object') return false;
    const record = result as Record<string, unknown>;
    const request = requests[index];
    return (
      record.intake_item_id === request.intake_item_id &&
      record.document_reference_id === request.document_reference_id &&
      record.extraction_id === request.extraction_id &&
      record.field_id === request.field_id &&
      (record.outcome === 'accepted' ||
        record.outcome === 'conflict' ||
        record.outcome === 'not_found')
    );
  });
}

export function IntakeExperience({
  context = PROVIDER_CONTEXT,
  onDirtyChange,
}: IntakeExperienceProps) {
  const isAdmin = context.kind === 'admin';
  const userRoles = useUserRoles();
  // Intake scope follows authenticated membership, never the editable form role.
  const providerView = userRoles.includes(ROLES.PRACTICE_ADMINISTRATOR) ? 'all' : 'individual';
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { intakeHub } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const getDocumentUrl = useArtifactDownload();
  const [config, setConfig] = useState<IntakeConfig>(defaultIntakeConfig);
  const [acknowledgedConfig, setAcknowledgedConfig] = useState<IntakeConfig>(defaultIntakeConfig);
  const [hydratedConfigurationScope, setHydratedConfigurationScope] = useState<string | null>(null);
  const adminStep = new URLSearchParams(location.search).get('intakeStep');
  const routedPageKey = isAdmin
    ? resolveIntakePageKey(`/intake/${adminStep ?? ''}`)
    : resolveIntakePageKey(location.pathname);
  const currentKey = routedPageKey ?? 'K1';
  const [gateOpenedInSession, setGateOpenedInSession] = useState(false);
  const [fieldStates, setFieldStates] = useState<IntakeFieldStates>({});
  const [discardedSuggestionIds, setDiscardedSuggestionIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [isSavingAdminChanges, setIsSavingAdminChanges] = useState(false);
  const [adminSaveErrorCount, setAdminSaveErrorCount] = useState(0);
  const [railValidationRequestedPage, setRailValidationRequestedPage] = useState<string | null>(
    null,
  );
  const [docKinds, setDocKinds] = useState<
    Record<string, { docKind: string | null; filled: number }>
  >({});

  const { data: providerPayerOptions } = useGetIntakeFormPayerOptionsQuery(undefined, {
    skip: isAdmin,
  });
  const { data: adminPayerOptions } = useGetAdminIntakeFormPayerOptionsQuery(
    {
      organizationId: context.kind === 'admin' ? context.organizationId : '',
      userId: context.kind === 'admin' ? context.userId : undefined,
    },
    { skip: !isAdmin },
  );
  const payerOptions = isAdmin ? adminPayerOptions : providerPayerOptions;
  const configurationScope =
    context.kind === 'admin'
      ? `${context.organizationId}:${context.userId ?? 'organization'}`
      : 'provider';
  const {
    currentData: providerConfiguration,
    error: providerConfigurationError,
    isFetching: isProviderConfigurationFetching,
    refetch: refetchProviderConfiguration,
  } = useGetIntakeFormConfigurationQuery(undefined, { skip: isAdmin });
  const {
    currentData: adminConfiguration,
    error: adminConfigurationError,
    isFetching: isAdminConfigurationFetching,
    refetch: refetchAdminConfiguration,
  } = useGetAdminIntakeFormConfigurationQuery(
    {
      organizationId: context.kind === 'admin' ? context.organizationId : '',
      userId: context.kind === 'admin' ? context.userId : undefined,
    },
    { skip: !isAdmin },
  );
  const serverConfiguration = isAdmin ? adminConfiguration : providerConfiguration;
  const gateOpen = gateOpenedInSession || Boolean(serverConfiguration?.last_change);
  const configurationError = isAdmin ? adminConfigurationError : providerConfigurationError;
  const isConfigurationFetching = isAdmin
    ? isAdminConfigurationFetching
    : isProviderConfigurationFetching;
  const refetchConfiguration = isAdmin ? refetchAdminConfiguration : refetchProviderConfiguration;
  const {
    data: providerDashboard,
    error: providerDashboardError,
    isFetching: isProviderDashboardFetching,
    refetch: refetchProviderDashboard,
  } = useGetIntakeHubDashboardQuery({ experience: 'v2', view: providerView }, { skip: isAdmin });
  const {
    data: adminDashboard,
    error: adminDashboardError,
    isFetching: isAdminDashboardFetching,
    refetch: refetchAdminDashboard,
  } = useGetAdminIntakeHubDashboardQuery(
    {
      organizationId: context.kind === 'admin' ? context.organizationId : '',
      userId: context.kind === 'admin' ? context.userId : undefined,
      experience: 'v2',
    },
    { skip: !isAdmin },
  );
  const dashboard = isAdmin ? adminDashboard : providerDashboard;
  const dashboardError = isAdmin ? adminDashboardError : providerDashboardError;
  const isDashboardFetching = isAdmin ? isAdminDashboardFetching : isProviderDashboardFetching;
  const refetchDashboard = isAdmin ? refetchAdminDashboard : refetchProviderDashboard;
  const { data: providerDocumentInventory, refetch: refetchProviderDocumentInventory } =
    useGetIntakeHubDocumentsQuery({ experience: 'v2' }, { skip: isAdmin || !dashboard });
  const { data: adminDocumentInventory, refetch: refetchAdminDocumentInventory } =
    useGetAdminIntakeHubDocumentsQuery(
      {
        organizationId: context.kind === 'admin' ? context.organizationId : '',
        userId: context.kind === 'admin' ? context.userId : undefined,
      },
      { skip: !isAdmin || !dashboard },
    );
  const documentInventory = isAdmin ? adminDocumentInventory : providerDocumentInventory;
  const refetchDocumentInventory = isAdmin
    ? refetchAdminDocumentInventory
    : refetchProviderDocumentInventory;
  const [generateProviderForm, { data: providerForm }] = useGenerateIntakeFormMutation();
  const [generateAdminForm, { data: adminForm }] = useGenerateAdminIntakeFormMutation();
  const form = isAdmin ? adminForm : providerForm;
  const [updateProviderEvidence] = useUpdateIntakeHubItemEvidenceMutation();
  const [updateAdminEvidence] = useUpdateAdminIntakeHubItemEvidenceMutation();
  const [updateProviderConfiguration] = useUpdateIntakeFormConfigurationMutation();
  const [updateAdminConfiguration] = useUpdateAdminIntakeFormConfigurationMutation();
  const [updateSensitiveField] = useUpdateIntakeHubSensitiveFieldMutation();
  const [acceptDocumentFieldSuggestions] = useAcceptIntakeHubDocumentFieldSuggestionsMutation();
  const writeQueue = useSerializedIntakeWrites();
  const acceptanceOperationTail = useRef<Promise<void>>(Promise.resolve());
  const acceptanceOperations = useRef(new Map<string, Promise<void>>());
  const acceptedSuggestionRequestIds = useRef(new Set<string>());
  const fieldStatesRef = useRef(fieldStates);
  fieldStatesRef.current = fieldStates;
  const {
    triggerAutoSave: triggerProviderConfigurationSave,
    status: providerConfigurationSaveStatus,
    lastSavedAt: providerConfigurationLastSavedAt,
    hasPendingSave: hasPendingProviderConfigurationSave,
  } = useAutoSave<IntakeConfig>({
    enabled: !isAdmin && hydratedConfigurationScope === configurationScope,
    debounceMs: 800,
    flushOnUnmount: true,
    onSave: async nextConfiguration => {
      await updateProviderConfiguration({
        intakeFormConfigurationUpdateRequest: intakeConfigurationUpdate(nextConfiguration),
      }).unwrap();
      setAcknowledgedConfig(nextConfiguration);
    },
  });

  const organizationId = dashboard?.clinician?.organization_id;
  const userId = dashboard?.clinician?.user_id;
  const assignedProducts = useMemo<IntakeConfig['products']>(() => {
    const products = new Set<IntakeConfig['products'][number]>();
    const orderedProducts: IntakeConfig['products'] = ['jotcred', 'jotbill'];
    for (const enrollment of dashboard?.enrollments ?? []) {
      if (enrollment.service_key === 'jotcred' || enrollment.service_key === 'jotbill') {
        products.add(enrollment.service_key);
      }
    }
    return orderedProducts.filter(product => products.has(product));
  }, [dashboard?.enrollments]);
  const effectiveConfig = useMemo<IntakeConfig>(
    () => ({ ...config, products: assignedProducts }),
    [assignedProducts, config],
  );
  const shouldLoadEligibleProviders =
    effectiveConfig.practiceType === 'group' && effectiveConfig.role !== 'provider';
  // The clinician-role member list is only the candidate pool for the picker.
  // The roster itself is the explicit list stored in the Intake Hub tables.
  const {
    clinicians: providerRosterCandidatePool,
    isLoading: providerCandidatesLoading,
    error: providerCandidatesQueryError,
  } = useClinicians({ skip: isAdmin || !shouldLoadEligibleProviders });
  const contextEligibleProviders = context.kind === 'admin' ? context.eligibleProviders : undefined;
  const rosterCandidatePool = useMemo(
    () => (isAdmin ? (contextEligibleProviders ?? []) : providerRosterCandidatePool),
    [contextEligibleProviders, isAdmin, providerRosterCandidatePool],
  );
  const providerRosterEntries = useMemo(
    () => dashboard?.provider_roster ?? [],
    [dashboard?.provider_roster],
  );
  const eligibleProviders = useMemo<EligibleProvider[]>(() => {
    const namesByUserId = new Map(
      rosterCandidatePool.map(candidate => [candidate.userId, candidate.name]),
    );
    return providerRosterEntries.map(entry => ({
      userId: entry.user_id,
      name: namesByUserId.get(entry.user_id) ?? entry.user_id,
    }));
  }, [providerRosterEntries, rosterCandidatePool]);
  const rosterCandidates = useMemo(() => {
    const rosteredUserIds = new Set(providerRosterEntries.map(entry => entry.user_id));
    return rosterCandidatePool.filter(candidate => !rosteredUserIds.has(candidate.userId));
  }, [providerRosterEntries, rosterCandidatePool]);
  const eligibleProvidersLoading =
    context.kind === 'admin'
      ? false
      : providerCandidatesLoading || (isDashboardFetching && !dashboard);
  const eligibleProvidersQueryError =
    context.kind === 'admin' ? undefined : providerCandidatesQueryError;
  const canManageProviderRoster = useCan('manage_members');
  const canManageRoster = isAdmin || canManageProviderRoster;
  const [addProviderRosterEntry] = useAddIntakeProviderRosterEntryMutation();
  const [removeProviderRosterEntry] = useRemoveIntakeProviderRosterEntryMutation();
  const [addAdminProviderRosterEntry] = useAddAdminIntakeProviderRosterEntryMutation();
  const [removeAdminProviderRosterEntry] = useRemoveAdminIntakeProviderRosterEntryMutation();
  const handleAddRosterProvider = useCallback(
    (rosterUserId: string) => {
      if (context.kind === 'admin') {
        void addAdminProviderRosterEntry({
          organizationId: context.organizationId,
          intakeHubProviderRosterAddRequest: { user_id: rosterUserId },
        });
        return;
      }
      void addProviderRosterEntry({
        intakeHubProviderRosterAddRequest: { user_id: rosterUserId },
      });
    },
    [addAdminProviderRosterEntry, addProviderRosterEntry, context],
  );
  const handleRemoveRosterProvider = useCallback(
    (rosterUserId: string) => {
      if (context.kind === 'admin') {
        void removeAdminProviderRosterEntry({
          organizationId: context.organizationId,
          userId: rosterUserId,
        });
        return;
      }
      void removeProviderRosterEntry({ userId: rosterUserId });
    },
    [context, removeAdminProviderRosterEntry, removeProviderRosterEntry],
  );
  const hasUnsavedAdminChanges =
    isAdmin &&
    (!configurationsMatch(config, acknowledgedConfig) ||
      Object.values(fieldStates).some(
        state => JSON.stringify(state.draftValue) !== JSON.stringify(state.acknowledgedValue),
      ));

  const discardAdminDrafts = useCallback(() => {
    setFieldStates(previous =>
      Object.fromEntries(
        Object.entries(previous).map(([fieldId, state]) => [
          fieldId,
          { ...state, draftValue: state.acknowledgedValue },
        ]),
      ),
    );
    setConfig(acknowledgedConfig);
    setAdminSaveErrorCount(0);
  }, [acknowledgedConfig]);

  const navigateToPage = useCallback(
    (pageKey: string, replace = false) => {
      if (isAdmin) {
        if (
          hasUnsavedAdminChanges &&
          !window.confirm('Discard unsaved Intake Hub changes and continue?')
        ) {
          return;
        }
        if (hasUnsavedAdminChanges) discardAdminDrafts();
        const search = new URLSearchParams(location.search);
        const slug = intakePagePath(pageKey).split('/').at(-1) ?? 'key-questions';
        search.set('intakeStep', slug);
        navigate({ pathname: location.pathname, search: search.toString() }, { replace });
        return;
      }
      navigate(
        {
          pathname: intakePagePath(pageKey),
          search: location.search,
        },
        { replace },
      );
    },
    [
      discardAdminDrafts,
      hasUnsavedAdminChanges,
      isAdmin,
      location.pathname,
      location.search,
      navigate,
    ],
  );

  useEffect(() => {
    onDirtyChange?.(hasUnsavedAdminChanges);
  }, [hasUnsavedAdminChanges, onDirtyChange]);

  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  useEffect(() => {
    if (!hasUnsavedAdminChanges) return undefined;
    const preventUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [hasUnsavedAdminChanges]);

  useEffect(() => {
    if (!routedPageKey) navigateToPage('K1', true);
  }, [navigateToPage, routedPageKey]);

  useEffect(() => {
    const defaults = defaultIntakeConfig();
    setConfig(defaults);
    setAcknowledgedConfig(defaults);
    setHydratedConfigurationScope(null);
  }, [configurationScope]);

  useEffect(() => {
    if (!serverConfiguration) return;
    const hydrated = serverConfigurationToIntakeConfig(serverConfiguration);
    setConfig(previous => (configurationsMatch(previous, hydrated) ? previous : hydrated));
    setAcknowledgedConfig(previous =>
      configurationsMatch(previous, hydrated) ? previous : hydrated,
    );
    setHydratedConfigurationScope(configurationScope);
  }, [configurationScope, serverConfiguration]);

  useEffect(() => {
    if (
      assignedProducts.length === 0 ||
      hydratedConfigurationScope !== configurationScope ||
      configurationError
    )
      return;
    const generateIntakeFormRequest = {
      role: effectiveConfig.role,
      products: effectiveConfig.products,
      practice_type: effectiveConfig.practiceType,
      is_prescriber: effectiveConfig.isPrescriber,
      license_types: effectiveConfig.licenseTypes,
      payers: effectiveConfig.payerKeys.map(payer_key => ({
        payer_key,
        states: [],
        plan_types: [],
      })),
    };
    if (context.kind === 'admin') {
      generateAdminForm({
        organizationId: context.organizationId,
        userId: context.userId,
        generateIntakeFormRequest,
      });
    } else {
      generateProviderForm({ generateIntakeFormRequest });
    }
  }, [
    assignedProducts.length,
    configurationError,
    configurationScope,
    context,
    effectiveConfig,
    generateAdminForm,
    generateProviderForm,
    hydratedConfigurationScope,
  ]);

  const allFields = useMemo(
    () =>
      (form?.canvases ?? [])
        .filter(canvas => canvas.is_active)
        .flatMap(canvas => canvas.fields ?? []),
    [form],
  );
  const fieldPageIndex = useMemo(() => {
    const index: Record<string, string> = {};
    for (const canvas of form?.canvases ?? []) {
      if (!canvas.is_active || !canvas.canvas_key) continue;
      for (const field of canvas.fields ?? []) index[field.field_id] = canvas.canvas_key;
    }
    return index;
  }, [form]);
  const activeSuggestionFieldIds = useMemo(
    () => allFields.filter(canDisplayDocumentSuggestion).map(field => field.field_id),
    [allFields],
  );
  const { documentSuggestionReferenceIds, documentSuggestions } = useMemo(() => {
    const indexed = new Map<string, IntakeFieldSuggestion>();
    const referenceIds = new Map<string, string>();
    const conflicts = new Set<string>();
    const activeIds = new Set(activeSuggestionFieldIds);
    if (isAdmin) return { documentSuggestionReferenceIds: {}, documentSuggestions: {} };
    for (const documentItem of documentInventory?.documents ?? []) {
      for (const suggestion of documentItem.field_suggestions ?? []) {
        const fieldState = fieldStates[suggestion.field_id];
        if (
          !activeIds.has(suggestion.field_id) ||
          conflicts.has(suggestion.field_id) ||
          discardedSuggestionIds.has(suggestionIdentity(suggestion)) ||
          (fieldState &&
            (JSON.stringify(fieldState.draftValue) !==
              JSON.stringify(fieldState.acknowledgedValue) ||
              JSON.stringify(fieldState.draftValue) === JSON.stringify(suggestion.value)))
        )
          continue;
        const existing = indexed.get(suggestion.field_id);
        if (existing && JSON.stringify(existing.value) !== JSON.stringify(suggestion.value)) {
          indexed.delete(suggestion.field_id);
          referenceIds.delete(suggestion.field_id);
          conflicts.add(suggestion.field_id);
          continue;
        }
        indexed.set(suggestion.field_id, suggestion);
        referenceIds.set(
          suggestion.field_id,
          documentItem.document_reference.document_reference_id,
        );
      }
    }
    return {
      documentSuggestionReferenceIds: Object.fromEntries(referenceIds),
      documentSuggestions: Object.fromEntries(indexed),
    };
  }, [
    activeSuggestionFieldIds,
    discardedSuggestionIds,
    documentInventory?.documents,
    fieldStates,
    isAdmin,
  ]);
  const suggestionsByPage = useMemo(() => {
    const counts: Record<string, { total: number; unconfirmed: number }> = {};
    for (const suggestion of Object.values(documentSuggestions)) {
      const pageKey = fieldPageIndex[suggestion.field_id];
      if (!pageKey) continue;
      const count = counts[pageKey] ?? { total: 0, unconfirmed: 0 };
      count.total += 1;
      count.unconfirmed += 1;
      counts[pageKey] = count;
    }
    return counts;
  }, [documentSuggestions, fieldPageIndex]);
  const landedSummary = useMemo(() => {
    const parts = ORDERED_KEYS.flatMap(pageKey => {
      const count = suggestionsByPage[pageKey]?.unconfirmed ?? 0;
      return count > 0 ? [`${count} field${count === 1 ? '' : 's'} on ${pageLabel(pageKey)}`] : [];
    });
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [suggestionsByPage]);
  const firstPageWithSuggestions = useMemo(
    () => ORDERED_KEYS.find(pageKey => (suggestionsByPage[pageKey]?.unconfirmed ?? 0) > 0) ?? null,
    [suggestionsByPage],
  );
  useEffect(() => {
    setFieldStates(previous =>
      hydrateFieldStates(allFields, dashboard?.items ?? [], previous, userId),
    );
  }, [allFields, dashboard?.items, userId]);

  // ---- uploads -----------------------------------------------------------
  const { constraints: providerUploadConstraints } = useArtifactUploadConstraints();
  const { constraints: adminUploadConstraints } = useAdminArtifactUploadConstraints();
  const constraints = isAdmin ? adminUploadConstraints : providerUploadConstraints;
  const { upload: uploadProviderFiles } = useUploadOrganizationArtifacts(organizationId);
  const { upload: uploadAdminFiles } = useUploadAdminOrganizationArtifacts(
    context.kind === 'admin' ? context.organizationId : '',
  );
  const {
    pendingUploads,
    trackUpload,
    dismissByArtifactId,
    reconcile,
    handleArtifactEvent,
    handleDocRefExtraction,
  } = useOptimisticUploads({ retainUntilReconciled: true });
  const [uploadingFileCount, setUploadingFileCount] = useState(0);

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (!organizationId) return;
      const { accepted } = validateFiles(files, constraints);
      if (accepted.length === 0) return;
      const upload = isAdmin ? uploadAdminFiles : uploadProviderFiles;
      setUploadingFileCount(accepted.length);
      let outcome: Awaited<ReturnType<typeof upload>>;
      try {
        outcome = await upload(accepted, {
          sourceChannel: isAdmin ? 'intake_hub_admin' : 'intake_hub',
          intakeHub:
            userId && form?.catalog_version
              ? {
                  ...(currentKey === 'M1' ? { scope: 'organization' as const } : { userId }),
                  catalogVersion: form.catalog_version,
                  activeFieldIds: activeSuggestionFieldIds,
                }
              : undefined,
        });
      } catch (error) {
        enqueueSnackbar(formatUploadLimitError(error), { variant: 'error' });
        return;
      } finally {
        setUploadingFileCount(0);
      }
      if (outcome) {
        trackUpload(outcome);
        void refetchDocumentInventory();
      }
    },
    [
      activeSuggestionFieldIds,
      constraints,
      currentKey,
      enqueueSnackbar,
      form?.catalog_version,
      isAdmin,
      organizationId,
      refetchDocumentInventory,
      trackUpload,
      uploadAdminFiles,
      uploadProviderFiles,
      userId,
    ],
  );

  const { isDragging } = useChartWideDropZone({
    enabled: Boolean(organizationId) && currentKey in DOCUMENT_PAGES,
    onDrop: files => {
      void handleFiles(files);
    },
  });
  const isUploadInFlight = uploadingFileCount > 0;

  const handleArtifactStreamEvent = useCallback(
    (event: Parameters<typeof handleArtifactEvent>[0]) => {
      handleArtifactEvent(event);
      void refetchDocumentInventory();
    },
    [handleArtifactEvent, refetchDocumentInventory],
  );

  useOrganizationArtifactEventStream(organizationId, {
    enabled: !isAdmin && Boolean(organizationId),
    onEvent: handleArtifactStreamEvent,
    onDocRefExtraction: event => {
      handleDocRefExtraction(event);
      void refetchDocumentInventory();
      const summary = event.summary ?? {};
      const artifactId = event.artifactId ?? '';
      if (artifactId) {
        setDocKinds(prev => ({
          ...prev,
          [artifactId]: {
            docKind: (summary as { doc_kind?: string }).doc_kind ?? null,
            filled:
              summary.extracted_fields && typeof summary.extracted_fields === 'object'
                ? Object.keys(summary.extracted_fields).length
                : 0,
          },
        }));
      }
    },
  });
  useAdminOrganizationArtifactEventStream(context.kind === 'admin' ? context.organizationId : '', {
    enabled: isAdmin && Boolean(organizationId),
    onEvent: handleArtifactStreamEvent,
    onDocRefExtraction: event => {
      handleDocRefExtraction(event);
      void refetchDocumentInventory();
    },
  });

  useEffect(() => {
    reconcile(
      (documentInventory?.documents ?? []).map(documentItem => documentItem.document_reference),
    );
  }, [documentInventory?.documents, reconcile]);

  // ---- derived -----------------------------------------------------------
  const pageStats = useMemo(() => {
    const stats: Record<string, { fieldCount: number; requiredCount: number; active: boolean }> =
      {};
    for (const canvas of form?.canvases ?? []) {
      stats[canvas.canvas_key ?? ''] = {
        fieldCount: canvas.fields?.length ?? 0,
        requiredCount: canvas.required_count ?? 0,
        active: canvas.is_active,
      };
    }
    return stats;
  }, [form]);

  const notNeededKeys = useMemo(
    () =>
      form
        ? new Set(
            ORDERED_KEYS.filter(key => !['K1', 'F1'].includes(key) && !pageStats[key]?.active),
          )
        : new Set<string>(),
    [pageStats, form],
  );

  useEffect(() => {
    if (!form || !routedPageKey || ['K1', 'F1'].includes(routedPageKey)) return;
    const pageExists = (form.canvases ?? []).some(canvas => canvas.canvas_key === routedPageKey);
    if (!pageExists) navigateToPage('K1', true);
  }, [form, navigateToPage, routedPageKey]);

  useEffect(() => {
    // F1 stays reachable while the gate is closed, matching IntakeShell's
    // unlock rule and the stale-route guard above.
    if (!serverConfiguration || gateOpen || ['K1', 'F1'].includes(currentKey)) return;
    navigateToPage('K1', true);
  }, [currentKey, gateOpen, navigateToPage, serverConfiguration]);

  const payerRows = useMemo(
    () =>
      config.payerKeys.map(payerKey => {
        const payer = (payerOptions?.payers ?? []).find(entry => entry.payer_key === payerKey);
        return {
          payerKey,
          displayName: payer?.display_name ?? payerKey,
          states: '—',
          plans: '—',
        };
      }),
    [config.payerKeys, payerOptions],
  );

  const derivedSentence = useMemo(() => {
    const flags = form?.derived_flags;
    if (!flags) return null;
    const parts: string[] = [];
    if (flags.has_medicare && flags.has_medicaid)
      parts.push("Medicare and Medicaid detected → we'll ask for a PTAN and a Medicaid ID later.");
    else if (flags.has_medicare) parts.push("Medicare detected → we'll ask for a PTAN later.");
    else if (flags.has_medicaid)
      parts.push("Medicaid detected → we'll ask for a Medicaid ID later.");
    if (flags.payer_requires_references)
      parts.push('A selected payer needs professional references before we can file there.');
    if (flags.payer_requires_portal_login) parts.push('A selected payer needs a portal login.');
    return parts.length > 0 ? parts.join(' ') : null;
  }, [form]);

  const totalRequired = useMemo(
    () =>
      (form?.canvases ?? [])
        .filter(canvas => canvas.is_active)
        .reduce((sum, canvas) => sum + (canvas.required_count ?? 0), 0),
    [form],
  );

  const documentsToGather: ForecastDocument[] = useMemo(() => {
    const activeDocumentFields = (form?.canvases ?? [])
      .filter(
        canvas =>
          canvas.is_active &&
          canvas.canvas_type === 'upload' &&
          ['clinic_setup', 'your_intake'].includes(canvas.section),
      )
      .flatMap(canvas => canvas.fields ?? [])
      .filter(
        field => field.data_type === 'file' && field.destination.kind === 'document_reference',
      );
    const grouped = new Map<
      string,
      ForecastDocument & { forecastOrder: number; sourceIndex: number }
    >();
    activeDocumentFields.forEach((field, sourceIndex) => {
      const presentation = field.document_forecast;
      const groupKey = presentation?.group_key ?? `field:${field.field_id}`;
      if (grouped.has(groupKey)) return;
      grouped.set(groupKey, {
        fieldId: groupKey,
        label: presentation?.label ?? field.label,
        detail: presentation?.detail ?? (field.help_text || field.condition || undefined),
        forecastOrder: presentation?.order ?? Number.MAX_SAFE_INTEGER,
        sourceIndex,
      });
    });
    return [...grouped.values()]
      .sort(
        (left, right) =>
          left.forecastOrder - right.forecastOrder || left.sourceIndex - right.sourceIndex,
      )
      .map(({ fieldId, label, detail }) => ({ fieldId, label, detail }));
  }, [form]);

  const percentComplete = useMemo(
    () => durableRequiredCompletion(allFields, fieldStates, dashboard?.items ?? [], userId).percent,
    [allFields, dashboard?.items, fieldStates, userId],
  );
  const hasUploadedClinicDocument =
    (documentInventory?.documents?.length ?? 0) > 0 || pendingUploads.length > 0;
  const progressByPage = useMemo(() => {
    const progress: Record<string, RailProgressState> = {};
    for (const canvas of form?.canvases ?? []) {
      if (!canvas.is_active || !canvas.canvas_key) continue;
      if (canvas.canvas_key === 'M1' && hasUploadedClinicDocument) {
        progress[canvas.canvas_key] = 'completed';
        continue;
      }
      const completion = durableRequiredCompletion(
        canvas.fields ?? [],
        fieldStates,
        dashboard?.items ?? [],
        userId,
      );
      progress[canvas.canvas_key] =
        completion.total > 0 && completion.complete === completion.total
          ? 'completed'
          : completion.complete > 0
            ? 'inProgress'
            : 'notStarted';
    }
    return progress;
  }, [dashboard?.items, fieldStates, form?.canvases, hasUploadedClinicDocument, userId]);
  const reviewItems = useMemo(() => {
    const sectionKey =
      currentKey === 'M6' ? 'clinic_setup' : currentKey === 'P6' ? 'your_intake' : null;
    if (!sectionKey) return [];
    const reviewPageKey = sectionKey === 'clinic_setup' ? 'M6' : 'P6';
    const section = RAIL_SECTIONS.find(entry => entry.key === sectionKey);
    return (section?.items ?? [])
      .filter(item => item.pageKey !== reviewPageKey && !notNeededKeys.has(item.pageKey))
      .map(item => ({
        pageKey: item.pageKey,
        label: item.label,
        state: progressByPage[item.pageKey] ?? 'notStarted',
      }));
  }, [currentKey, notNeededKeys, progressByPage]);

  const foundDocuments: FoundDocument[] = useMemo(() => {
    const persisted = (documentInventory?.documents ?? []).map(documentItem =>
      persistedDocumentToFoundDocument(documentItem, allFields),
    );
    const persistedArtifactIds = new Set(
      (documentInventory?.documents ?? []).flatMap(documentItem => {
        const artifactId = documentItem.document_reference.source_artifact_id;
        return artifactId ? [artifactId] : [];
      }),
    );
    const optimistic = pendingUploads
      .filter(pending => !persistedArtifactIds.has(pending.artifactId))
      .map(pending => {
        const info = docKinds[pending.artifactId];
        return {
          artifactId: pending.artifactId,
          filename: pending.filename,
          status: documentDisplayStatus(pending.status),
          docKind: info?.docKind ?? null,
          filledCount: info?.filled ?? 0,
          understood: pending.status !== 'failed',
        };
      });
    return [...optimistic, ...persisted];
  }, [pendingUploads, docKinds, documentInventory?.documents, allFields]);
  const isDocumentProcessing =
    isUploadInFlight || foundDocuments.some(document => document.status === 'parsing');

  // ---- navigation --------------------------------------------------------
  const goNext = useCallback(() => {
    const index = ORDERED_KEYS.indexOf(currentKey);
    for (let next = index + 1; next < ORDERED_KEYS.length; next += 1) {
      const key = ORDERED_KEYS[next];
      if (!notNeededKeys.has(key)) {
        navigateToPage(key);
        return;
      }
    }
  }, [currentKey, navigateToPage, notNeededKeys]);

  const goBack = useCallback(() => {
    const index = ORDERED_KEYS.indexOf(currentKey);
    for (let previous = index - 1; previous >= 0; previous -= 1) {
      const key = ORDERED_KEYS[previous];
      if (!notNeededKeys.has(key)) {
        navigateToPage(key);
        return;
      }
    }
  }, [currentKey, navigateToPage, notNeededKeys]);

  const nextLabel = useMemo(() => {
    const index = ORDERED_KEYS.indexOf(currentKey);
    for (let next = index + 1; next < ORDERED_KEYS.length; next += 1) {
      const key = ORDERED_KEYS[next];
      if (notNeededKeys.has(key)) continue;
      const label = RAIL_SECTIONS.flatMap(section => section.items).find(
        item => item.pageKey === key,
      )?.label;
      return `Next: ${label} →`;
    }
    return 'Finish →';
  }, [currentKey, notNeededKeys]);

  const currentLabel =
    RAIL_SECTIONS.flatMap(section => section.items).find(item => item.pageKey === currentKey)
      ?.label ?? currentKey;

  const currentPage = useMemo(
    () => (form?.canvases ?? []).find(canvas => canvas.canvas_key === currentKey),
    [form, currentKey],
  );
  const currentFields = useMemo(() => currentPage?.fields ?? [], [currentPage?.fields]);
  const currentGroups = currentPage?.groups ?? [];
  const payerRosterField = useMemo(
    () => allFields.find(field => field.group_id === 'I13'),
    [allFields],
  );
  const payerRosterItem = payerRosterField
    ? findIntakeItemForField(payerRosterField, dashboard?.items ?? [], userId)
    : undefined;
  const payerRoster = payerRosterItem?.evidence_summary?.payer_roster ?? [];

  const draftValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fieldStates).map(([fieldId, state]) => [fieldId, state.draftValue]),
      ),
    [fieldStates],
  );
  const acknowledgedValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fieldStates).map(([fieldId, state]) => [fieldId, state.acknowledgedValue]),
      ),
    [fieldStates],
  );
  const hasAcknowledgedValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fieldStates).map(([fieldId, state]) => [
          fieldId,
          state.hasAcknowledgedValue,
        ]),
      ),
    [fieldStates],
  );
  const disabledReasons = useMemo(() => {
    const reasons: Record<string, string> = {};
    for (const field of allFields) {
      // IntakePageFieldInput owns catalog-declared read-only fields. Everything
      // writable is fail-closed here unless this component has a persistence
      // branch for the exact destination below.
      if (field.is_writable === false) continue;

      if (field.destination.kind === 'intake_item_evidence') {
        const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
        if (!field.requirement_key || !item?.intake_item_id) {
          reasons[field.field_id] = 'This field is still being prepared. Refresh and try again.';
        }
        continue;
      }

      if (field.destination.kind === 'intake_item_sensitive') {
        if (isAdmin) {
          reasons[field.field_id] =
            'The provider must complete this field through the secure flow.';
          continue;
        }
        const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
        if (!field.requirement_key || !item?.intake_item_id) {
          reasons[field.field_id] = 'This field is still being prepared. Refresh and try again.';
        }
        continue;
      }

      // intake_config, document_reference, and future destination kinds do
      // not have a generic page write path in handlePersistField.
      reasons[field.field_id] = 'Complete this information in its dedicated Intake Hub section.';
    }
    return reasons;
  }, [allFields, dashboard?.items, isAdmin, userId]);

  const invalidFormatPageKeys = useMemo(
    () =>
      new Set(
        (form?.canvases ?? []).flatMap(canvas => {
          if (!canvas.is_active) return [];
          const hasFormatError = (canvas.fields ?? []).some(field => {
            const key = field.field_id || field.field_key;
            if (field.is_writable === false || disabledReasons[key]) return false;
            const state = fieldStates[key];
            const value = state?.draftValue ?? state?.acknowledgedValue ?? defaultFieldValue(field);
            return Boolean(getIntakeFieldFormatError(field, value, state?.acknowledgedValue));
          });
          return hasFormatError ? [canvas.canvas_key] : [];
        }),
      ),
    [disabledReasons, fieldStates, form?.canvases],
  );
  const navigateFromRail = useCallback(
    (pageKey: string) => {
      const currentIndex = ORDERED_KEYS.indexOf(currentKey);
      const targetIndex = ORDERED_KEYS.indexOf(pageKey);
      const blockingPageKey = ORDERED_KEYS.find(
        (candidate, index) =>
          index >= currentIndex && index < targetIndex && invalidFormatPageKeys.has(candidate),
      );
      if (blockingPageKey) {
        setRailValidationRequestedPage(blockingPageKey);
        if (blockingPageKey !== currentKey) navigateToPage(blockingPageKey);
        return;
      }
      navigateToPage(pageKey);
    },
    [currentKey, invalidFormatPageKeys, navigateToPage],
  );

  const handleDraftChange = useCallback(
    (field: GeneratedIntakeField, value: IntakeFieldValue) => {
      setFieldStates(previous =>
        updateFieldStatesForDraftChange(allFields, previous, field, value),
      );
    },
    [allFields],
  );

  const handlePersistField = useCallback(
    async (field: GeneratedIntakeField, value: IntakeFieldValue) => {
      if (!isAdmin) {
        intakeHub.fieldEditAttempted({
          destination_kind: field.destination.kind,
          field_id: field.field_id,
          field_type: field.data_type,
        });
      }

      if (field.destination.kind === 'intake_item_evidence') {
        const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
        if (!item?.intake_item_id || !form?.catalog_version) {
          throw new Error('Intake field destination is unavailable');
        }
        const intakeItemId = item.intake_item_id;
        await writeQueue.enqueue(intakeItemId, () =>
          context.kind === 'admin'
            ? updateAdminEvidence({
                organizationId: context.organizationId,
                userId: context.userId,
                intakeItemId,
                updateItemEvidenceRequest: {
                  catalog_version: form.catalog_version,
                  extracted_fields: { [field.destination.field_key]: value },
                },
              }).unwrap()
            : updateProviderEvidence({
                intakeItemId,
                updateItemEvidenceRequest: {
                  catalog_version: form.catalog_version,
                  extracted_fields: { [field.destination.field_key]: value },
                },
              }).unwrap(),
        );
        setFieldStates(previous => acknowledgePersistedFieldValue(previous, field.field_id, value));
        return;
      }

      if (field.destination.kind === 'intake_item_sensitive') {
        if (isAdmin) throw new Error('Secure fields are provider-only');
        const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
        if (!item?.intake_item_id || typeof value !== 'string') {
          throw new Error('Secure field destination is unavailable');
        }
        const intakeItemId = item.intake_item_id;
        const fieldKey = field.destination.field_key;
        await writeQueue.enqueue(intakeItemId, () =>
          updateSensitiveField({
            intakeItemId,
            updateSensitiveFieldRequest: {
              field_key: fieldKey,
              value,
            },
          }).unwrap(),
        );
        setFieldStates(previous =>
          acknowledgePersistedFieldValue(previous, field.field_id, value, true),
        );
        void refetchDashboard();
        return;
      }

      throw new Error('Intake field destination is not writable here');
    },
    [
      form?.catalog_version,
      context,
      dashboard?.items,
      isAdmin,
      refetchDashboard,
      intakeHub,
      updateAdminEvidence,
      updateProviderEvidence,
      updateSensitiveField,
      writeQueue,
      userId,
    ],
  );

  const handlePersistPayerRoster = useCallback(
    async (rows: IntakePayerRosterEntry[]) => {
      if (!payerRosterItem?.intake_item_id || !form?.catalog_version) {
        throw new Error('Payer roster destination is unavailable');
      }
      if (!isAdmin) {
        intakeHub.fieldEditAttempted({
          destination_kind: 'intake_item_evidence',
          field_id: 'I13',
          field_type: 'payer_roster',
        });
      }
      const intakeItemId = payerRosterItem.intake_item_id;
      await writeQueue.enqueue(intakeItemId, () =>
        context.kind === 'admin'
          ? updateAdminEvidence({
              organizationId: context.organizationId,
              userId: context.userId,
              intakeItemId,
              updateItemEvidenceRequest: {
                catalog_version: form.catalog_version,
                payer_roster: rows,
              },
            }).unwrap()
          : updateProviderEvidence({
              intakeItemId,
              updateItemEvidenceRequest: {
                catalog_version: form.catalog_version,
                payer_roster: rows,
              },
            }).unwrap(),
      );
      void refetchDashboard();
    },
    [
      context,
      form?.catalog_version,
      intakeHub,
      isAdmin,
      payerRosterItem?.intake_item_id,
      refetchDashboard,
      updateAdminEvidence,
      updateProviderEvidence,
      writeQueue,
    ],
  );

  const handleConfigurationChange = useCallback(
    (patch: Partial<IntakeConfig>) => {
      setConfig(previous => {
        const next = { ...previous, ...patch };
        if (!isAdmin) triggerProviderConfigurationSave(next);
        return next;
      });
    },
    [isAdmin, triggerProviderConfigurationSave],
  );

  const handleSaveAdminChanges = useCallback(async () => {
    const dirtyFields = currentFields.filter(field => {
      const state = fieldStates[field.field_id];
      return (
        state &&
        JSON.stringify(state.draftValue) !== JSON.stringify(state.acknowledgedValue) &&
        !disabledReasons[field.field_id]
      );
    });
    const configurationIsDirty = !configurationsMatch(config, acknowledgedConfig);
    if (dirtyFields.length === 0 && !configurationIsDirty) return;

    setIsSavingAdminChanges(true);
    setAdminSaveErrorCount(0);
    const saves = dirtyFields.map(field =>
      handlePersistField(field, fieldStates[field.field_id].draftValue),
    );
    if (configurationIsDirty && context.kind === 'admin') {
      saves.push(
        updateAdminConfiguration({
          organizationId: context.organizationId,
          userId: context.userId,
          intakeFormConfigurationUpdateRequest: intakeConfigurationUpdate(config),
        })
          .unwrap()
          .then(() => setAcknowledgedConfig(config)),
      );
    }
    const results = await Promise.allSettled(saves);
    setAdminSaveErrorCount(results.filter(result => result.status === 'rejected').length);
    setIsSavingAdminChanges(false);
  }, [
    acknowledgedConfig,
    config,
    context,
    currentFields,
    disabledReasons,
    fieldStates,
    handlePersistField,
    updateAdminConfiguration,
  ]);

  const resolveAcceptedSuggestionRequests = useCallback(
    (suggestions: IntakeFieldSuggestion[]): IntakeFieldSuggestionAcceptanceRequest[] =>
      suggestions.map(suggestion => {
        const documentReferenceId = documentSuggestionReferenceIds[suggestion.field_id];
        if (!documentReferenceId) throw new Error('Document suggestion is unavailable');
        return {
          intake_item_id: suggestion.target_intake_item_id,
          document_reference_id: documentReferenceId,
          extraction_id: suggestion.extraction_id,
          field_id: suggestion.field_id,
        };
      }),
    [documentSuggestionReferenceIds],
  );

  const enqueueSuggestionAcceptance = useCallback(
    (operationId: string, write: () => Promise<void>): Promise<void> => {
      const existing = acceptanceOperations.current.get(operationId);
      if (existing) return existing;

      const operation = acceptanceOperationTail.current.then(write);
      acceptanceOperations.current.set(operationId, operation);
      const normalizedTail = operation.then(
        () => undefined,
        () => undefined,
      );
      acceptanceOperationTail.current = normalizedTail;
      void normalizedTail.then(() => {
        if (acceptanceOperationTail.current === normalizedTail) {
          acceptedSuggestionRequestIds.current.clear();
        }
      });
      void operation
        .finally(() => {
          if (acceptanceOperations.current.get(operationId) === operation) {
            acceptanceOperations.current.delete(operationId);
          }
        })
        .catch(() => undefined);
      return operation;
    },
    [],
  );

  const persistAcceptedSuggestions = useCallback(
    async (
      suggestions: IntakeFieldSuggestion[],
      requests: IntakeFieldSuggestionAcceptanceRequest[],
    ) => {
      if (isAdmin) throw new Error('Document suggestions are provider-only');
      const response = await writeQueue.enqueueMany(
        suggestions.map(suggestion => suggestion.target_intake_item_id),
        () =>
          acceptDocumentFieldSuggestions({
            acceptDocumentFieldSuggestionsRequest: { suggestions: requests },
          }).unwrap(),
      );
      return { requests, response };
    },
    [acceptDocumentFieldSuggestions, isAdmin, writeQueue],
  );

  const handleAcceptSuggestion = useCallback(
    (suggestion: IntakeFieldSuggestion) => {
      let requests: IntakeFieldSuggestionAcceptanceRequest[];
      try {
        requests = resolveAcceptedSuggestionRequests([suggestion]);
      } catch {
        void refetchDashboard();
        void refetchDocumentInventory();
        return Promise.reject(new SuggestionAcceptanceError('uncertain'));
      }
      const requestId = acceptanceRequestIdentity(requests[0]);
      const operationId = acceptanceOperationIdentity('individual', requests);

      return enqueueSuggestionAcceptance(operationId, async () => {
        if (acceptedSuggestionRequestIds.current.has(requestId)) return;
        intakeHub.suggestionActionClicked({ action: 'accept', suggestion_count: 1 });
        let persisted;
        try {
          persisted = await persistAcceptedSuggestions([suggestion], requests);
        } catch {
          void refetchDashboard();
          void refetchDocumentInventory();
          throw new SuggestionAcceptanceError('uncertain');
        }
        if (!hasExactlyCorrelatedAcceptanceResults(persisted.requests, persisted.response)) {
          void refetchDashboard();
          void refetchDocumentInventory();
          throw new SuggestionAcceptanceError('uncertain');
        }
        if (persisted.response.results[0].outcome !== 'accepted') {
          void refetchDashboard();
          void refetchDocumentInventory();
          throw new SuggestionAcceptanceError('stale');
        }
        acceptedSuggestionRequestIds.current.add(requestId);
        setFieldStates(previous =>
          acknowledgePersistedFieldValue(previous, suggestion.field_id, suggestion.value),
        );
      });
    },
    [
      enqueueSuggestionAcceptance,
      intakeHub,
      persistAcceptedSuggestions,
      refetchDashboard,
      refetchDocumentInventory,
      resolveAcceptedSuggestionRequests,
    ],
  );

  const handleDiscardSuggestion = useCallback(
    (suggestion: IntakeFieldSuggestion) => {
      intakeHub.suggestionActionClicked({ action: 'discard', suggestion_count: 1 });
      setDiscardedSuggestionIds(previous => {
        const next = new Set(previous);
        next.add(suggestionIdentity(suggestion));
        return next;
      });
    },
    [intakeHub],
  );

  const rollbackOptimisticSuggestions = useCallback(
    (
      failedIds: Set<string>,
      suggestions: IntakeFieldSuggestion[],
      previousStates: IntakeFieldStates,
    ) => {
      setFieldStates(previous =>
        Object.fromEntries(
          Object.entries(previous).flatMap(([fieldId, state]) => {
            if (!failedIds.has(fieldId)) return [[fieldId, state]];
            const suggestion = suggestions.find(candidate => candidate.field_id === fieldId);
            const stillShowsOptimisticValue =
              suggestion &&
              JSON.stringify(state.draftValue) === JSON.stringify(suggestion.value) &&
              JSON.stringify(state.acknowledgedValue) === JSON.stringify(suggestion.value);
            if (!stillShowsOptimisticValue) return [[fieldId, state]];
            const prior = previousStates[fieldId];
            return prior ? [[fieldId, prior]] : [];
          }),
        ),
      );
    },
    [],
  );

  const handleUseAllSuggestions = useCallback(
    (suggestions: IntakeFieldSuggestion[]) => {
      if (suggestions.length === 0) return Promise.resolve();
      let requests: IntakeFieldSuggestionAcceptanceRequest[];
      try {
        requests = resolveAcceptedSuggestionRequests(suggestions);
      } catch {
        void refetchDashboard();
        void refetchDocumentInventory();
        return Promise.reject(new SuggestionAcceptanceError('uncertain'));
      }
      const operationId = acceptanceOperationIdentity('bulk', requests);

      return enqueueSuggestionAcceptance(operationId, async () => {
        intakeHub.suggestionActionClicked({
          action: 'accept_all',
          suggestion_count: suggestions.length,
        });
        const pendingEntries = suggestions.flatMap((suggestion, index) => {
          const request = requests[index];
          return acceptedSuggestionRequestIds.current.has(acceptanceRequestIdentity(request))
            ? []
            : [{ request, suggestion }];
        });
        if (pendingEntries.length === 0) return;
        const pendingRequests = pendingEntries.map(entry => entry.request);
        const pendingSuggestions = pendingEntries.map(entry => entry.suggestion);
        const previousStates: IntakeFieldStates = Object.fromEntries(
          pendingSuggestions.flatMap(suggestion => {
            const previous = fieldStatesRef.current[suggestion.field_id];
            return previous ? [[suggestion.field_id, previous]] : [];
          }),
        );
        setFieldStates(previous => ({
          ...previous,
          ...Object.fromEntries(
            pendingSuggestions.map(suggestion => [
              suggestion.field_id,
              {
                acknowledgedValue: suggestion.value,
                draftValue: suggestion.value,
                hasAcknowledgedValue: true,
              },
            ]),
          ),
        }));

        let persisted;
        try {
          persisted = await persistAcceptedSuggestions(pendingSuggestions, pendingRequests);
        } catch {
          const failedIds = new Set(pendingSuggestions.map(suggestion => suggestion.field_id));
          void refetchDashboard();
          void refetchDocumentInventory();
          rollbackOptimisticSuggestions(failedIds, pendingSuggestions, previousStates);
          throw new SuggestionAcceptanceError('uncertain');
        }
        if (!hasExactlyCorrelatedAcceptanceResults(persisted.requests, persisted.response)) {
          const failedIds = new Set(pendingSuggestions.map(suggestion => suggestion.field_id));
          void refetchDashboard();
          void refetchDocumentInventory();
          rollbackOptimisticSuggestions(failedIds, pendingSuggestions, previousStates);
          throw new SuggestionAcceptanceError('uncertain');
        }

        persisted.response.results.forEach((result, index) => {
          if (result.outcome === 'accepted') {
            acceptedSuggestionRequestIds.current.add(
              acceptanceRequestIdentity(persisted.requests[index]),
            );
          }
        });
        const failedIds = new Set(
          persisted.response.results.flatMap((result, index) =>
            result.outcome === 'accepted' ? [] : [pendingSuggestions[index].field_id],
          ),
        );
        if (failedIds.size === 0) return;

        rollbackOptimisticSuggestions(failedIds, pendingSuggestions, previousStates);
        void refetchDashboard();
        void refetchDocumentInventory();
        throw new SuggestionAcceptanceError('stale');
      });
    },
    [
      enqueueSuggestionAcceptance,
      intakeHub,
      persistAcceptedSuggestions,
      refetchDashboard,
      refetchDocumentInventory,
      resolveAcceptedSuggestionRequests,
      rollbackOptimisticSuggestions,
    ],
  );

  const handleHelpAction = useCallback(
    (action: 'message_specialist') => {
      if (action === 'message_specialist') dispatch(openChatWindow());
    },
    [dispatch],
  );

  if (dashboardError && !dashboard) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 6 }}>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={isDashboardFetching}
              onClick={() => void refetchDashboard()}
            >
              Retry
            </Button>
          }
        >
          We couldn&apos;t load your intake. Please try again.
        </Alert>
      </Box>
    );
  }

  if (configurationError && hydratedConfigurationScope !== configurationScope) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 6 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetchConfiguration()}>
              Retry
            </Button>
          }
        >
          We couldn&apos;t load this intake configuration. No changes have been made.
        </Alert>
      </Box>
    );
  }

  if (
    (isConfigurationFetching && hydratedConfigurationScope !== configurationScope) ||
    hydratedConfigurationScope !== configurationScope ||
    !serverConfiguration
  ) {
    return <LinearProgress aria-label="Loading intake configuration" />;
  }

  return (
    <IntakeShell
      organizationName={dashboard?.clinician?.organization_id ? 'Your practice' : 'Your practice'}
      currentPageKey={currentKey}
      notNeededKeys={notNeededKeys}
      gateOpen={gateOpen}
      percentComplete={percentComplete}
      onNavigate={navigateFromRail}
      pendingByPage={suggestionsByPage}
      progressByPage={progressByPage}
    >
      {currentKey === 'K1' && (
        <>
          {!isAdmin && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <AutosaveStatus
                status={providerConfigurationSaveStatus}
                isDirty={hasPendingProviderConfigurationSave()}
                lastSavedAt={providerConfigurationLastSavedAt}
                verbose
              />
            </Box>
          )}
          <IntakeConfigPage
            config={effectiveConfig}
            onChange={handleConfigurationChange}
            payerOptions={payerOptions?.payers ?? []}
            payerRows={payerRows}
            eligibleProviders={eligibleProviders}
            eligibleProvidersLoading={eligibleProvidersLoading}
            eligibleProvidersError={Boolean(eligibleProvidersQueryError)}
            rosterCandidates={rosterCandidates}
            canManageRoster={canManageRoster}
            onAddRosterProvider={handleAddRosterProvider}
            onRemoveRosterProvider={handleRemoveRosterProvider}
            derivedSentence={derivedSentence}
            groups={currentGroups}
            onHelpAction={handleHelpAction}
            onContinue={() => {
              setGateOpenedInSession(true);
              navigateToPage('F1');
            }}
            saveLabel={isAdmin ? (isSavingAdminChanges ? 'Saving…' : 'Save changes') : undefined}
            onSave={isAdmin ? () => void handleSaveAdminChanges() : undefined}
            saveDisabled={
              isAdmin && (isSavingAdminChanges || configurationsMatch(config, acknowledgedConfig))
            }
            saveError={isAdmin && adminSaveErrorCount > 0}
          />
        </>
      )}

      {currentKey === 'F1' && (
        <IntakeForecastPage
          pages={pageStats}
          documents={documentsToGather}
          totalRequired={totalRequired}
          onContinue={goNext}
          onBack={goBack}
          nextLabel={nextLabel}
        />
      )}

      {currentKey === 'M4' && (
        <IntakePayersPage
          payerRows={payerRows}
          initialRoster={payerRoster}
          fields={currentFields}
          groups={currentGroups}
          draftValues={draftValues}
          acknowledgedValues={acknowledgedValues}
          onDraftChange={handleDraftChange}
          onPersistField={handlePersistField}
          onPersistRoster={handlePersistPayerRoster}
          onContinue={goNext}
          onBack={goBack}
          nextLabel={nextLabel}
          onHelpAction={handleHelpAction}
          disabledReasons={disabledReasons}
          saveMode={isAdmin ? 'explicit' : 'autosave'}
          isSaving={isSavingAdminChanges}
          saveErrorCount={adminSaveErrorCount}
          onSaveChanges={isAdmin ? handleSaveAdminChanges : undefined}
          saveDisabled={isAdmin && configurationsMatch(config, acknowledgedConfig)}
        />
      )}

      {(currentKey === 'M6' || currentKey === 'P6') && (
        <IntakeReviewSubmitPage
          sectionLabel={currentKey === 'M6' ? 'Clinic setup' : 'Your intake'}
          items={reviewItems}
          onNavigate={navigateToPage}
          onContinue={goNext}
          onBack={goBack}
          nextLabel={nextLabel}
        />
      )}

      {currentKey in DOCUMENT_PAGES && (
        <IntakeDocumentsPage
          title={DOCUMENT_PAGES[currentKey].title}
          sub={DOCUMENT_PAGES[currentKey].sub}
          dropHint={DOCUMENT_PAGES[currentKey].hint}
          documents={foundDocuments}
          onDismissFailed={dismissByArtifactId}
          getDocumentUrl={getDocumentUrl}
          isDragging={isDragging}
          isProcessing={isDocumentProcessing}
          onFiles={handleFiles}
          landedSummary={landedSummary}
          onReview={
            firstPageWithSuggestions ? () => navigateToPage(firstPageWithSuggestions) : undefined
          }
          reviewLabel={
            firstPageWithSuggestions ? `Review ${pageLabel(firstPageWithSuggestions)} →` : undefined
          }
          groups={currentGroups}
          onHelpAction={handleHelpAction}
          onContinue={goNext}
          onBack={goBack}
          nextLabel={nextLabel}
        />
      )}

      {!['K1', 'F1', 'M4', 'M6', 'P6'].includes(currentKey) && !(currentKey in DOCUMENT_PAGES) && (
        <>
          <IntakeFieldsPage
            title={currentLabel}
            fields={currentFields}
            groups={currentGroups}
            suggestions={documentSuggestions}
            onAcceptSuggestion={handleAcceptSuggestion}
            onDiscardSuggestion={handleDiscardSuggestion}
            onUseAllSuggestions={handleUseAllSuggestions}
            onContinue={goNext}
            onBack={goBack}
            nextLabel={nextLabel}
            draftValues={draftValues}
            acknowledgedValues={acknowledgedValues}
            hasAcknowledgedValues={hasAcknowledgedValues}
            onDraftChange={handleDraftChange}
            onPersistField={handlePersistField}
            onHelpAction={handleHelpAction}
            disabledReasons={disabledReasons}
            saveMode={isAdmin ? 'explicit' : 'autosave'}
            isSaving={isSavingAdminChanges}
            saveErrorCount={adminSaveErrorCount}
            onSaveChanges={isAdmin ? () => void handleSaveAdminChanges() : undefined}
            validationRequested={railValidationRequestedPage === currentKey}
          />
        </>
      )}

      {(isDragging || isUploadInFlight) && (
        <ArtifactDropOverlay
          subtitle={isUploadInFlight ? 'Keep this page open while the upload finishes.' : undefined}
          isUploading={isUploadInFlight}
          uploadingMessage={`Uploading ${uploadingFileCount} file${
            uploadingFileCount === 1 ? '' : 's'
          }`}
        />
      )}
    </IntakeShell>
  );
}
